import {
  fetchAssociationsWithSwap,
  generateCacheKey,
} from "@/util/associationUtils";
import { ASSOCIATION_CONSTANTS } from "@/util/constants";
import { hasContactFields } from "@/util/customObjectUtils";

/**
 * Minimal shape of the store required for ensuring valid contact association
 */
interface StoreLike {
  contactAssociationSettings: {
    selectedAssociation?: string[] | string;
    objectModel?: string;
    withObjectModel?: string;
    firstObjectKey?: string;
    secondObjectKey?: string;
  } | null;
  currentCustomObjectModel: string | null;
  locationId: string;
  elements: any[];
  associationCache: Map<string, any>;
  anyUnsavedChanges: boolean;
}

/**
 * Extract an array of association ids from a heterogeneous associations array
 * Accepts entries in either cached option shape ({ value }) or API shape ({ id })
 */
const extractAssociationIds = (associations: any[]): string[] => {
  if (!Array.isArray(associations)) {
    return [];
  }
  return associations
    .map((item) => (item?.value ? String(item.value) : item?.id ? String(item.id) : ""))
    .filter((id) => Boolean(id)) as string[];
};

/**
 * Ensure the saved contact association remains valid with latest associations.
 * If the saved association was deleted externally, pick the first available association
 * for the current custom object model and persist it.
 *
 * @param store - Pinia app store subset
 * @returns Promise<void>
 */
export const ensureValidContactAssociation = async (store: StoreLike): Promise<void> => {
  const saved = store.contactAssociationSettings;
  const currentModel = store.currentCustomObjectModel || saved?.objectModel;
  if (!currentModel || !store.locationId) {
    return;
  }

  // Object-pair associations validate against the paired object; contact-mode
  // associations validate against contact.
  const isPair =
    !!saved?.withObjectModel &&
    saved.withObjectModel !== ASSOCIATION_CONSTANTS.CONTACT_OBJECT_TYPE;
  const withObjectKey = isPair
    ? (saved!.withObjectModel as string)
    : ASSOCIATION_CONSTANTS.CONTACT_OBJECT_TYPE;

  // Contact mode only enforces when contact fields are present; pair mode always validates.
  if (!isPair) {
    const hasContacts = hasContactFields(store.elements, null);
    if (!hasContacts) {
      return;
    }
  }

  // If no saved selection yet, nothing to validate here
  const savedSelection = Array.isArray(saved?.selectedAssociation)
    ? saved?.selectedAssociation?.[0]
    : (saved?.selectedAssociation as string | undefined);

  // Load associations from cache or API (namespaced by target to match the dialog)
  const cacheKey = generateCacheKey(
    isPair ? `${currentModel}__${withObjectKey}` : currentModel,
    store.locationId
  );
  const cachedData = store.associationCache.get(cacheKey);
  const isCacheExpired =
    cachedData && Date.now() - cachedData.timestamp > ASSOCIATION_CONSTANTS.CACHE_EXPIRY_MS;

  let optionObjects: any[] = [];
  if (cachedData && !isCacheExpired) {
    optionObjects = cachedData.associations || [];
  } else {
    try {
      // Shared fetch (with swapped-direction retry) — same source as the dialog.
      optionObjects = await fetchAssociationsWithSwap(
        currentModel,
        withObjectKey,
        store.locationId
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch associations while ensuring validity:", err);
      return;
    }
  }

  const optionIds = extractAssociationIds(optionObjects);
  if (!optionIds.length) {
    return;
  }

  const exists = savedSelection && optionIds.includes(String(savedSelection));
  if (exists) {
    return;
  }

  // Saved selection missing or invalid -> pick first option and persist
  const nextValue = optionIds[0];
  if (nextValue) {
    // Carry the chosen association's object keys so the backend can link records.
    const chosen = optionObjects.find(
      (option) => String(option?.value ?? option?.id) === String(nextValue)
    );
    store.contactAssociationSettings = {
      selectedAssociation: [nextValue],
      objectModel: currentModel,
      ...(chosen?.firstObjectKey ? { firstObjectKey: chosen.firstObjectKey } : {}),
      ...(chosen?.secondObjectKey ? { secondObjectKey: chosen.secondObjectKey } : {}),
      ...(isPair ? { withObjectModel: withObjectKey } : {}),
    } as any;
    store.anyUnsavedChanges = true;
  }
};
